import { WIDGET_CONSTANTS } from '../constants/widget-constants';
import { AdyenBookingContext } from '../models/adyen.model';
import { CartItem, EventCartItem } from '../models/cart.model';

/** What a payment surface knows about the booking it is about to take money for. */
export interface AdyenBookingSource {
  widgetType?: string;
  cart?: CartItem[];
  eventCart?: EventCartItem[];
  storeId?: number;
  /** Booking-link mode: the booking is stamped with the link's owner, not the slot's worker. */
  bookingLinkUuid?: string;
  /** Pre-defined slots book an existing appointment, addressed by its uuid. */
  predefinedSlotUuid?: string;
  /**
   * The widget-level flag `/book` itself branches on when choosing `worker_id`.
   *
   * Passed in rather than re-derived: it is true only when exactly one SERVICE is selected and
   * that service is a combi, while Adyen's own gate is one CART ITEM - and a single cart item
   * can carry several services. Inferring "combi" from a non-empty mapping therefore disagreed
   * with `/book` on exactly that case, which is a wrong `worker_id`, a wrong store and a payment
   * taken on the wrong merchant account. `undefined` means the surface does not know, and the
   * legacy inference is kept for it.
   */
  isCombiServiceSelected?: boolean;
}

/**
 * The booking context the backend derives the booked store from - and so the Adyen merchant
 * account. The availability check and the session request MUST describe the same booking, so both
 * build it here and nowhere else.
 *
 * Only fields that name something are included: a 0 or an empty string would ask the backend to
 * guess, and the object doubles as a query string where `undefined` must not appear.
 */
export function buildAdyenBookingContext(source: AdyenBookingSource): AdyenBookingContext {
  const context: AdyenBookingContext = {};

  if (source.widgetType === WIDGET_CONSTANTS.EVENT || source.widgetType === WIDGET_CONSTANTS.EVENTS) {
    assign(context, 'slot_id', positiveId(source.eventCart?.[0]?.slotId));
    return context;
  }

  assign(context, 'store_id', positiveId(source.storeId));
  assign(context, 'worker_id', bookedWorkerId(source.cart?.[0], source.isCombiServiceSelected));
  assign(context, 'booking_link_uuid', nonEmpty(source.bookingLinkUuid));
  assign(context, 'predefined_slot_uuid', nonEmpty(source.predefinedSlotUuid));
  return context;
}

/**
 * Identifies the booking a held (Pending) Adyen payment was for, so the hold covers that booking
 * and nothing else: the booking context the session was minted with, plus - for appointments -
 * the services and the slot, which that context does not carry (two slots of one worker share a
 * store and a worker).
 *
 * The amount is deliberately NOT part of it. A coupon changes the amount of the SAME booking, and
 * a scope that moved with the amount would let a rebuilt payment form mint a second payable session
 * for a booking whose first payment may still arrive.
 */
export function buildAdyenHoldScope(source: AdyenBookingSource): string {
  const item = source.cart?.[0];
  // Only an identity, never shown: the UTC day is as good as the local one for telling slots apart.
  const day = item?.date ? new Date(item.date).getTime() : Number.NaN;
  const slot = item
    ? {
      services: [...(item.servicesIds ?? [])].map(Number).sort((a, b) => a - b),
      day: Number.isNaN(day) ? undefined : new Date(day).toISOString().slice(0, 10),
      start: item.startTime,
    }
    : undefined;

  return JSON.stringify({ context: buildAdyenBookingContext(source), slot });
}

/** True when the context names the booking, i.e. an availability answer for it is store-specific. */
export function hasAdyenBookingContext(context: AdyenBookingContext): boolean {
  return Object.keys(context).length > 0;
}

/**
 * The worker `/book` sends for the cart's appointment - and it must be the SAME worker, because
 * the backend derives the booked store, and therefore the Adyen merchant account, from it.
 *
 * `/book` branches on `appointmentState.isCombiServiceSelected`: combi goes to the first worker of
 * the mapping, everything else to the cart item's own worker. Adyen is only offered for a
 * single-appointment cart, so the first item is the booking.
 *
 * When the flag is not supplied the legacy inference is kept ("a non-empty mapping means combi").
 * That is deliberately unchanged rather than silently corrected: it is right whenever the single
 * cart item also holds a single service, and the surfaces that can disagree are the ones now
 * passing the real flag.
 */
function bookedWorkerId(item: CartItem | undefined, isCombiServiceSelected?: boolean): number | undefined {
  const combiWorkerId = Object.values(item?.combiServiceWorkersMapping ?? {})[0];

  if (isCombiServiceSelected === true) {
    return positiveId(combiWorkerId);
  }

  if (isCombiServiceSelected === false) {
    return positiveId(item?.workerId);
  }

  return positiveId(combiWorkerId ?? item?.workerId);
}

function positiveId(id: unknown): number | undefined {
  const value = Number(id);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

function nonEmpty(value: string | undefined): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function assign<K extends keyof AdyenBookingContext>(
  context: AdyenBookingContext,
  key: K,
  value: AdyenBookingContext[K] | undefined,
): void {
  if (value !== undefined) {
    context[key] = value;
  }
}
